import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Divide as Hamburger } from "hamburger-react";
import { X, ArrowRight, ChevronDown, Moon, Sun, PhoneCall, Calculator, Network, Bot, Headphones, Briefcase } from "lucide-react";
import { logo } from "../../data/constant";
import { services } from "../../data/services";
import { useTheme } from "../ThemeContext";
import { getContextualWhatsAppUrl } from "../../utils/whatsappHelper";

const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

const serviceLinks = services.map(service => ({
  name: service.title,
  path: `/services/${slugify(service.title)}`
}));

const aiEmployeeRoleLinks = [
  {
    name: "AI Sales SDR",
    path: "/ai-employees/sales-sdr",
    desc: "Autonomous outbound dialing & ICP lead qualification",
    badge: "162ms SLA",
    icon: PhoneCall
  },
  {
    name: "AI Customer Support",
    path: "/ai-employees/customer-support",
    desc: "24/7 deterministic ticket resolution & live triage",
    badge: "<30s Triage",
    icon: Headphones
  },
  {
    name: "AI Receptionist & Concierge",
    path: "/ai-employees/receptionist-concierge",
    desc: "Sub-350ms voice front desk with PMS booking",
    badge: "Voice AI",
    icon: Bot
  },
  {
    name: "AI Operations Manager",
    path: "/ai-employees/operations-manager",
    desc: "Autonomous workflow orchestration & CRM reconciliation",
    badge: "Zero Drift",
    icon: Network
  },
  {
    name: "AI Executive Assistant",
    path: "/ai-employees/executive-assistant",
    desc: "Inbox zero, calendar defense & meeting intelligence",
    badge: "100% Privacy",
    icon: Briefcase
  }
];

const toolLinks = [
  {
    name: "Voice AI Agent Demo",
    path: "https://call.panthm.com",
    isExternal: true,
    desc: "Live sub-340ms WebRTC conversational demo",
    icon: PhoneCall
  },
  {
    name: "AI ROI Calculator",
    path: "/tools/ai-roi-calculator",
    desc: "Calculate savings for automated SDRs & support",
    icon: Calculator
  },
  {
    name: "Sovereign Mesh Simulator",
    path: "/#sovereign-mesh",
    desc: "Live load & latency benchmark simulator",
    icon: Network
  }
];

const links = [
  { name: "About Us", path: "/about-us" },
  { name: "Services", path: "/services", hasDropdown: true, type: "services" },
  { name: "Pricing", path: "/pricing", badge: "Plans" },
  { name: "Voice AI Demo", path: "https://call.panthm.com", isExternal: true, badge: "Live" },
  { name: "Portfolio", path: "/portfolio" },
  { name: "Blogs", path: "/blogs" },
  { name: "Contact", path: "/contact" },
];

const Header = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { pathname } = useLocation();
  const dropdownContainerRef = useRef(null);

  const handleNavigation = (path) => {
    setIsOpen(false);
    setActiveDropdown(null);

    if (path.includes("#")) {
      const [targetPath, hash] = path.split("#");
      const isCurrentHome = pathname === "/" || targetPath === "" || targetPath === "/";

      if (isCurrentHome && pathname === "/") {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 50);
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.setAttribute("data-drawer-open", "true");
    } else {
      document.body.style.overflow = "unset";
      document.body.removeAttribute("data-drawer-open");
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.removeAttribute("data-drawer-open");
    };
  }, [isOpen]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "py-3 bg-white/80 dark:bg-[#050505]/80 backdrop-blur-md shadow-lg shadow-slate-200/50 dark:shadow-none dark:border-b dark:border-white/10"
          : "py-5 bg-transparent"
      }`}
    >
      <div className="wrapper flex items-center justify-between">
        <Link to="/" className="cursor-pointer z-50" aria-label="PANTHM AI Labs Home">
          <img
            src={logo}
            alt="PANTHM AI Labs Logo"
            width={180}
            height={48}
            className="h-10 md:h-12 object-contain dark:invert dark:hue-rotate-180 transition-all duration-300"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav 
          ref={dropdownContainerRef}
          className="hidden lg:flex items-center gap-4 xl:gap-6 bg-white/60 dark:bg-white/5 backdrop-blur-md px-5 xl:px-7 py-2.5 rounded-full border border-white/40 dark:border-white/10 shadow-sm dark:shadow-none"
        >
          {links.map((link) =>
            link.hasDropdown ? (
              <div key={link.name} className="relative">
                <button
                  onClick={() => setActiveDropdown(activeDropdown === link.type ? null : link.type)}
                  className={`text-sm font-semibold transition-all duration-300 hover:text-primary flex items-center gap-1.5 py-1 whitespace-nowrap ${
                    link.type === "services" &&
                    (pathname.startsWith("/services") ||
                      pathname.startsWith("/ai-employees") ||
                      pathname.startsWith("/tools"))
                      ? "text-primary"
                      : "text-slate-700 dark:text-slate-200"
                  }`}
                >
                  {link.name}
                  {link.badge && (
                    <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-primary/15 text-primary border border-primary/20">
                      {link.badge}
                    </span>
                  )}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${
                      activeDropdown === link.type ? "rotate-180 text-primary" : ""
                    }`}
                  />
                </button>

                {/* Unified Services Mega-Dropdown */}
                {link.type === "services" && activeDropdown === "services" && (
                  <div className="absolute top-full left-0 lg:-left-20 xl:-left-32 mt-3 w-[740px] xl:w-[800px] bg-white dark:bg-[#090d16] rounded-3xl shadow-2xl dark:shadow-black/80 border border-slate-200/80 dark:border-white/10 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150 p-4">
                    <div className="grid grid-cols-12 gap-3.5">
                      
                      {/* Column 1: AI Digital Employees (Span 5) */}
                      <div className="col-span-5 bg-slate-50/70 dark:bg-white/[0.02] p-3 rounded-2xl border border-slate-100 dark:border-white/5 flex flex-col justify-between">
                        <div>
                          <div className="px-2 py-1 mb-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            <span>AI Digital Employees</span>
                            <span className="text-primary font-mono text-[9px] px-1.5 py-0.5 rounded bg-primary/10">Sub-200ms SLA</span>
                          </div>
                          <div className="space-y-1">
                            {aiEmployeeRoleLinks.map((role) => {
                              const Icon = role.icon;
                              return (
                                <Link
                                  key={role.path}
                                  to={role.path}
                                  onClick={() => setActiveDropdown(null)}
                                  className="flex items-start gap-2.5 p-2 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-primary/10 hover:text-primary dark:hover:text-white transition-all group"
                                >
                                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors flex-shrink-0 mt-0.5">
                                    <Icon size={14} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold flex items-center justify-between gap-1">
                                      <span>{role.name}</span>
                                      <span className="text-[9px] font-semibold px-1.5 py-0.2 rounded bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                                        {role.badge}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1">
                                      {role.desc}
                                    </div>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>

                        <div className="border-t border-slate-200/60 dark:border-white/5 mt-2 pt-2 px-2 flex items-center justify-between">
                          <Link
                            to="/services/ai-employees"
                            onClick={() => setActiveDropdown(null)}
                            className="text-[11px] font-bold uppercase tracking-wider text-primary hover:underline flex items-center gap-1"
                          >
                            Architecture Overview <ArrowRight size={11} />
                          </Link>
                          <Link
                            to="/solutions"
                            onClick={() => setActiveDropdown(null)}
                            className="text-[10px] font-medium text-slate-500 hover:text-primary dark:text-slate-400 transition-colors"
                          >
                            pSEO Matrix &rarr;
                          </Link>
                        </div>
                      </div>

                      {/* Column 2: Engineering & Cloud Services (Span 4) */}
                      <div className="col-span-4 p-2 flex flex-col justify-between">
                        <div>
                          <div className="px-2 py-1 mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            Engineering & Cloud
                          </div>
                          <div className="space-y-0.5">
                            {serviceLinks.map((svc) => (
                              <Link
                                key={svc.path}
                                to={svc.path}
                                onClick={() => setActiveDropdown(null)}
                                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-slate-700 dark:text-slate-300 hover:bg-primary/10 hover:text-primary dark:hover:text-primary transition-colors font-medium"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                                <span className="truncate">{svc.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div className="border-t border-slate-100 dark:border-white/5 mt-2 pt-2 px-2">
                          <Link
                            to="/services"
                            onClick={() => setActiveDropdown(null)}
                            className="text-[11px] font-bold uppercase tracking-wider text-primary hover:underline flex items-center justify-between"
                          >
                            <span>View All Services</span>
                            <ArrowRight size={12} />
                          </Link>
                        </div>
                      </div>

                      {/* Column 3: Live AI Tools & Pricing Spotlight (Span 3) */}
                      <div className="col-span-3 bg-gradient-to-br from-primary/5 via-slate-50 to-primary/10 dark:from-primary/10 dark:via-white/[0.02] dark:to-transparent p-3 rounded-2xl border border-primary/15 flex flex-col justify-between">
                        <div>
                          <div className="px-1 py-1 mb-1.5 text-[11px] font-bold uppercase tracking-wider text-primary flex items-center justify-between">
                            <span>Live AI Tools</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          </div>
                          <div className="space-y-1">
                            {toolLinks.map((tool) => {
                              const Icon = tool.icon;
                              return tool.isExternal || tool.path.startsWith("http") ? (
                                <a
                                  key={tool.name}
                                  href={tool.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setActiveDropdown(null)}
                                  className="flex items-start gap-2 p-2 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-white/10 hover:text-primary dark:hover:text-white transition-all group shadow-sm"
                                >
                                  <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors flex-shrink-0 mt-0.5">
                                    <Icon size={13} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-bold truncate">{tool.name}</div>
                                    <div className="text-[9px] text-slate-400 truncate">{tool.desc}</div>
                                  </div>
                                </a>
                              ) : (
                                <Link
                                  key={tool.path}
                                  to={tool.path}
                                  onClick={() => handleNavigation(tool.path)}
                                  className="flex items-start gap-2 p-2 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-white/10 hover:text-primary dark:hover:text-white transition-all group shadow-sm"
                                >
                                  <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors flex-shrink-0 mt-0.5">
                                    <Icon size={13} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-bold truncate">{tool.name}</div>
                                    <div className="text-[9px] text-slate-400 truncate">{tool.desc}</div>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>

                        {/* Pricing Highlight Card inside dropdown */}
                        <div className="mt-3 p-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white shadow-md">
                          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">AI Telecalling</div>
                          <div className="text-xs font-extrabold mt-0.5 flex items-center justify-between">
                            <span>Plans from ₹4,100</span>
                            <span className="text-[9px] bg-white/20 px-1 rounded">240 calls/hr</span>
                          </div>
                          <Link
                            to="/pricing"
                            onClick={() => setActiveDropdown(null)}
                            className="mt-2 text-[10px] font-bold flex items-center justify-between bg-white text-primary px-2.5 py-1 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <span>View All Plans</span>
                            <ArrowRight size={10} />
                          </Link>
                        </div>
                      </div>

                    </div>
                  </div>
                )}
              </div>
            ) : link.isExternal || link.path.startsWith("http") ? (
              <a
                key={link.name}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold transition-all duration-300 hover:text-primary relative group py-1 whitespace-nowrap flex items-center gap-1.5 text-slate-700 dark:text-slate-200"
              >
                {link.name}
                {link.badge && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {link.badge}
                  </span>
                )}
                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
              </a>
            ) : (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-semibold transition-all duration-300 hover:text-primary relative group py-1 whitespace-nowrap ${
                  pathname === link.path ? "text-primary" : "text-slate-700 dark:text-slate-200"
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-0.5 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full ${
                    pathname === link.path ? "w-full" : ""
                  }`}
                />
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <button 
            onClick={toggleTheme}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-all duration-300"
            aria-label="Toggle Dark Mode"
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          
          <a
            href={getContextualWhatsAppUrl(pathname)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex liquid-glass-button text-sm px-6 py-2.5 whitespace-nowrap"
          >
            Let's Talk <ArrowRight size={18} />
          </a>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden z-50 p-1 rounded-md focus:outline-none"
            aria-label="Toggle Mobile Menu"
          >
            <Hamburger
              toggled={isOpen}
              toggle={setIsOpen}
              size={24}
              color={isDarkMode ? "#ffffff" : "#0F172A"}
              rounded
            />
          </button>
        </div>
      </div>

      {/* ── Native Mobile Slide-over Drawer ── */}
      {/* 1. Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998] transition-opacity duration-300 lg:hidden ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* 2. Drawer Panel */}
      <div
        className={`fixed inset-y-0 right-0 h-[100dvh] w-[85%] max-w-[360px] bg-white dark:bg-[#07090e] text-slate-900 dark:text-white z-[99999] shadow-2xl border-l border-slate-200 dark:border-slate-800 transition-transform duration-300 ease-in-out transform lg:hidden flex flex-col pointer-events-auto ${
          isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Drawer Header */}
        <div className="flex justify-between items-center p-5 border-b border-slate-100 dark:border-slate-800/80 flex-shrink-0">
          <Link to="/" onClick={() => setIsOpen(false)}>
            <img 
              src={logo} 
              alt="PANTHM Logo" 
              width={140}
              height={38}
              className="h-8 object-contain dark:invert dark:hue-rotate-180 transition-all duration-300" 
            />
          </Link>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-full transition-colors cursor-pointer"
            aria-label="Close Menu"
          >
            <X size={22} className="text-slate-900 dark:text-slate-300" />
          </button>
        </div>

        {/* Featured Voice Demo Callout in Mobile Drawer */}
        <div className="px-5 pt-4 pb-1 flex-shrink-0">
          <a
            href="https://call.panthm.com"
            className="flex items-center justify-between p-3.5 rounded-2xl bg-gradient-to-r from-primary/15 via-emerald-500/10 to-primary/10 border border-primary/25 hover:border-primary/50 transition-all text-slate-900 dark:text-white group cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-md shadow-primary/30 group-hover:scale-105 transition-transform flex-shrink-0">
                <PhoneCall size={18} />
              </div>
              <div>
                <div className="text-sm font-bold flex items-center gap-1.5">
                  Voice AI Agent Demo
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                  Sub-340ms Conversational Call &rarr;
                </div>
              </div>
            </div>
            <ArrowRight size={16} className="text-primary group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </a>
        </div>

        {/* Scrollable Navigation Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-1 overscroll-contain">
          {links.map((link) =>
            link.hasDropdown ? (
              <div key={link.name} className="border-b border-slate-100 dark:border-slate-800/50">
                <button
                  type="button"
                  onClick={() => {
                    if (link.type === "services") setMobileServicesOpen((prev) => !prev);
                  }}
                  className="w-full flex items-center justify-between py-3.5 text-left cursor-pointer group"
                  aria-expanded={link.type === "services" ? mobileServicesOpen : false}
                >
                  <span
                    className={`text-xl font-bold transition-colors ${
                      link.type === "services" &&
                      (pathname.startsWith("/services") ||
                        pathname.startsWith("/ai-employees") ||
                        pathname.startsWith("/tools"))
                        ? "text-primary"
                        : "text-slate-900 dark:text-slate-100 group-hover:text-primary"
                    }`}
                  >
                    {link.name}
                  </span>
                  <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 group-hover:text-primary transition-all">
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${
                        link.type === "services" && mobileServicesOpen ? "rotate-180 text-primary" : ""
                      }`}
                    />
                  </div>
                </button>

                {/* Mobile Unified Services Accordion */}
                {link.type === "services" && mobileServicesOpen && (
                  <div className="ml-2 mb-3 flex flex-col gap-3 border-l-2 border-primary/40 pl-3 py-1 animate-in fade-in duration-150">
                    
                    {/* Section 1: AI Digital Employees */}
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-primary mb-1.5 flex items-center justify-between">
                        <span>AI Digital Employees</span>
                        <span className="text-[9px] px-1.5 py-0.2 rounded bg-primary/10 font-mono">162ms SLA</span>
                      </div>
                      <div className="space-y-0.5">
                        {aiEmployeeRoleLinks.map((role) => (
                          <Link
                            key={role.path}
                            to={role.path}
                            onClick={() => setIsOpen(false)}
                            className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors py-1.5 flex items-center justify-between text-left w-full cursor-pointer"
                          >
                            <span className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                              {role.name}
                            </span>
                            <span className="text-[9px] font-semibold px-1.5 py-0.2 rounded bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300">
                              {role.badge}
                            </span>
                          </Link>
                        ))}
                        <Link
                          to="/services/ai-employees"
                          onClick={() => setIsOpen(false)}
                          className="text-[11px] font-bold uppercase tracking-wider text-primary pt-1.5 flex items-center gap-1 hover:underline text-left w-full cursor-pointer block"
                        >
                          Workforce Architecture <ArrowRight size={11} />
                        </Link>
                      </div>
                    </div>

                    {/* Section 2: Core Engineering Services */}
                    <div className="border-t border-slate-100 dark:border-white/5 pt-2">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">
                        Engineering & Cloud
                      </div>
                      <div className="space-y-0.5">
                        {serviceLinks.map((svc) => (
                          <Link
                            key={svc.path}
                            to={svc.path}
                            onClick={() => setIsOpen(false)}
                            className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors py-1 flex items-center gap-2 text-left w-full cursor-pointer block"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                            {svc.name}
                          </Link>
                        ))}
                        <Link
                          to="/services"
                          onClick={() => setIsOpen(false)}
                          className="text-[11px] font-bold uppercase tracking-wider text-primary pt-1.5 flex items-center gap-1 hover:underline text-left w-full cursor-pointer block"
                        >
                          View All Services <ArrowRight size={11} />
                        </Link>
                      </div>
                    </div>

                    {/* Section 3: Live AI Tools & Simulators */}
                    <div className="border-t border-slate-100 dark:border-white/5 pt-2">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 flex items-center justify-between">
                        <span>Live AI Tools</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      </div>
                      <div className="space-y-1">
                        {toolLinks.map((tool) => {
                          if (tool.isExternal || tool.path.startsWith("http")) {
                            return (
                              <a
                                key={tool.name}
                                href={tool.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-primary transition-colors py-1.5 flex items-center justify-between text-left w-full cursor-pointer group"
                              >
                                <span className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                  {tool.name}
                                </span>
                                <span className="text-[9px] uppercase font-bold px-1.5 py-0.2 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                                  Live
                                </span>
                              </a>
                            );
                          }
                          return (
                            <Link
                              key={tool.path}
                              to={tool.path}
                              onClick={() => {
                                handleNavigation(tool.path);
                                setIsOpen(false);
                              }}
                              className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors py-1 flex items-center gap-2 text-left w-full cursor-pointer block"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                              {tool.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            ) : link.isExternal || link.path.startsWith("http") ? (
              <a
                key={link.name}
                href={link.path}
                className="text-xl font-bold transition-colors py-3.5 border-b border-slate-100 dark:border-slate-800/50 flex items-center justify-between w-full text-left text-slate-900 dark:text-slate-100 hover:text-primary dark:hover:text-primary group cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <PhoneCall size={18} className="text-primary" />
                  <span>{link.name}</span>
                </div>
                {link.badge && (
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    {link.badge}
                  </span>
                )}
              </a>
            ) : (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`text-xl font-bold transition-colors py-3.5 border-b border-slate-100 dark:border-slate-800/50 block w-full text-left cursor-pointer ${
                  pathname === link.path
                    ? "text-primary"
                    : "text-slate-900 dark:text-slate-100 hover:text-primary dark:hover:text-primary"
                }`}
              >
                {link.name}
              </Link>
            )
          )}
        </div>

        {/* Footer CTA Button */}
        <div className="p-6 border-t border-slate-100 dark:border-slate-800/80 flex-shrink-0 bg-white dark:bg-[#07090e]">
          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="primary-btn w-full justify-center py-4 text-base font-bold flex items-center gap-2 rounded-xl shadow-lg shadow-primary/20 cursor-pointer pointer-events-auto active:scale-95 transition-transform"
          >
            Start Project <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
